import React, { useState, useEffect } from 'react';
import CookieModal from './CookieModal';
import './CookieBanner.scss';

// Native cookie helpers
function getCookieValue(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

function setNativeCookie(name, value, days = 365) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`;
}

function hasCookieConsent() {
  return getCookieValue('cookie-consent') !== null;
}

// Cookie kategorileri
const COOKIE_CATEGORIES = [
  {
    id: 'RequiredCookies',
    name: 'Zorunlu Çerezler',
    description:
      'Zorunlu çerezler, İnternet Sitesi\u2019ni görüntülemeniz esnasında cihazınıza yerleştirilen ve sunulan online servislerin düzgün şekilde çalışabilmesi için gerekli olan çerezlerdir. Bu çerezlerin kullanımı esnasında gerçekleştirdiğimiz veri işleme faaliyetleri için Kanun\u2019un 5. maddesi kapsamında \u201c<strong>Bir sözleşmenin kurulması veya ifasıyla doğrudan doğruya ilgili olması kaydıyla, sözleşmenin taraflarına ait kişisel verilerin işlenmesinin gerekli olması</strong>\u201d veri işleme şartına dayanmaktayız.',
    required: true,
  },
  {
    id: 'FunctionalityCookies',
    name: 'İşlevsellik Çerezleri',
    description:
      'İşlevsellik çerezleri, İnternet Sitesi üzerindeki belirli işlevlerin sağlanması ve bunlara dair tercihlerinizin hatırlanması için gerekli olan çerezlerdir. İnternet Sitesi üzerinde dil tercihinizi hatırlamak ya da çerez kullanımına yönelik ayarlarınızı kaydetmek için işlevsellik çerezleri kullanılır. Bu çerezlerin kullanımı esnasında gerçekleştirdiğimiz veri işleme faaliyetleri için Kanun m.5 kapsamında veri işleme şartı olarak \u201c<strong>açık rızanıza</strong>\u201d dayanmaktayız.',
    required: false,
  },
  {
    id: 'PerformanceAnalyticsCookies',
    name: 'Performans ve Analitik Çerezleri',
    description:
      'Performans çerezleri, İnternet Sitesi ziyaret ve trafiğini takip ve analiz etmemizi sağlar. Bu çerezler sayesinde İnternet Sitesi üzerindeki alanlardan hangilerinin en sık ya da seyrek ziyaret edildiği gibi bilgileri edinebilir ve İnternet Sitesi\u2019nin trafiğini optimize edebiliriz. Bu çerezlerin kullanımı esnasında gerçekleştirdiğimiz veri işleme faaliyetleri için Kanun m.5 kapsamında veri işleme şartı olarak \u201c<strong>açık rızanıza</strong>\u201d dayanmaktayız.',
    required: false,
  },
  {
    id: 'TargetingAdvertisingCookies',
    name: 'Hedefleme ve Kişiselleştirilmiş Reklam Çerezleri',
    description:
      'Hedefleme ve kişiselleştirilmiş reklam çerezleri, sizlere İnternet Sitesinde veya İnternet Sitesi haricindeki mecralarda görüntüleme geçmişinize ve ziyaretçi profilinize uygun olarak kişiselleştirilmiş ürün ve hizmet tanıtımı yapmak için kullanılır. Bu çerezlerin kullanımı esnasında gerçekleştirdiğimiz veri işleme faaliyetleri için Kanun m.5 kapsamında veri işleme şartı olarak \u201c<strong>açık rızanıza</strong>\u201d dayanmaktayız.',
    required: false,
  },
];

function CookieBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [cookieOptions, setCookieOptions] = useState(() => {
    const initial = {};
    COOKIE_CATEGORIES.forEach((cat) => {
      initial[cat.id] = cat.required;
    });
    return initial;
  });

  useEffect(() => {
    // cookie-consent yoksa banner'ı göster
    if (!hasCookieConsent()) {
      setShowBanner(true);
    }
  }, []);

  const saveCookiePreferences = (options) => {
    setNativeCookie('cookie-consent', JSON.stringify(options));
    setShowBanner(false);
    setIsModalOpen(false);
  };

  const handleAcceptAll = () => {
    const allAccepted = {};
    COOKIE_CATEGORIES.forEach((cat) => {
      allAccepted[cat.id] = true;
    });
    setCookieOptions(allAccepted);
    saveCookiePreferences(allAccepted);
  };

  const handleRejectAll = () => {
    const allRejected = {};
    COOKIE_CATEGORIES.forEach((cat) => {
      allRejected[cat.id] = cat.required;
    });
    setCookieOptions(allRejected);
    saveCookiePreferences(allRejected);
  };

  const handleSaveSettings = () => {
    saveCookiePreferences(cookieOptions);
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleCloseBanner = () => {
    setShowBanner(false);
  };

  const toggleCookieOption = (id) => {
    const category = COOKIE_CATEGORIES.find((c) => c.id === id);
    if (category?.required) return;
    setCookieOptions((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  return (
    <>
      {/* Sabit Cookie İkonu - Sol Alt */}
      {!showBanner && (
        <button
          className="cookie-floating-btn"
          onClick={handleOpenModal}
          aria-label="Çerez ayarları"
          type="button"
          title="Çerez Ayarları"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
            <path d="M21.95 10.99c-1.79-.03-3.7-1.95-2.68-4.22-2.97 1-5.78-1.59-5.19-4.56C7.11.74 2 6.41 2 12c0 5.52 4.48 10 10 10 5.89 0 10.54-5.08 9.95-11.01zM8.5 15a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm2-5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm4.5 7a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z" />
          </svg>
        </button>
      )}

      {/* Cookie Banner */}
      {showBanner && (
        <div className="cookie-banner">
          <div className="cookie-banner__header">
            <div className="cookie-banner__header-content">
              <svg className="cookie-banner__icon" viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10c0-.34-.02-.675-.057-1.006a4.001 4.001 0 0 1-3.937-3.937A10.052 10.052 0 0 0 12 2zm-1.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3zm-3 5a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3zm5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3z" />
              </svg>
              <span className="cookie-banner__title">Çerez Kullanımı</span>
            </div>
            <button
              className="cookie-banner__close-btn"
              onClick={handleCloseBanner}
              aria-label="Çerez bildirimini kapat"
              type="button"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            </button>
          </div>
          <div className="cookie-banner__body">
            <p className="cookie-banner__text">
              Web sitemizde size en iyi deneyimi sunmak için çerezleri kullanıyoruz. Çerezler, web sitesinin düzgün
              çalışmasını sağlamak, deneyiminizi kişiselleştirmek ve trafiğimizi analiz etmek için kullanılmaktadır.
              Daha fazla bilgi için{' '}
              <a href="/cerez-politikasi" className="cookie-banner__link">
                Çerez Politikamızı
              </a>{' '}
              inceleyebilirsiniz.
            </p>
            <div className="cookie-banner__actions">
              <button className="cookie-banner__btn cookie-banner__btn--outline" onClick={handleOpenModal} type="button">
                Ayarları Yönet
              </button>
              <button className="cookie-banner__btn cookie-banner__btn--outline" onClick={handleRejectAll} type="button">
                Tümünü Reddet
              </button>
              <button className="cookie-banner__btn cookie-banner__btn--primary" onClick={handleAcceptAll} type="button">
                Tümünü Kabul Et
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cookie Modal */}
      {isModalOpen && (
        <CookieModal
          categories={COOKIE_CATEGORIES}
          cookieOptions={cookieOptions}
          onToggle={toggleCookieOption}
          onSave={handleSaveSettings}
          onAcceptAll={handleAcceptAll}
          onRejectAll={handleRejectAll}
          onClose={handleCloseModal}
        />
      )}
    </>
  );
}

export default CookieBanner;
